import React, { useEffect, useState } from 'react';
import { Users, UserCircle2, ChevronRight } from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import TeamMemberDetailModal from './TeamMemberDetailModal';
import { useTeamMembers } from '../../hooks/manager_portal_hooks';

const ManagerTeamMember = () => {
  const { setHeader } = usePageHeader();
  const { members: teamMembers, isLoading: teamLoading, error: teamError } = useTeamMembers();
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    setHeader({
      icon: Users,
      title: 'Team Member',
      subtitle: 'View and manage your team members and their details.',
    });
    return () => setHeader({ icon: null, title: '', subtitle: '' });
  }, [setHeader]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-1">People Reporting to Me &mdash; Summary</h2>
        <p className="text-sm text-gray-500 mb-4">Click a card to view employment and position details.</p>
        {teamLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
            <p className="ml-3 text-gray-600 text-sm">Loading your team...</p>
          </div>
        ) : teamError ? (
          <div className="text-center py-12">
            <p className="text-red-500 text-sm">Failed to load your team. Please try again.</p>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">You have no team members reporting to you.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {teamMembers.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => setSelectedMember(member)}
                className="border border-gray-200 rounded-lg p-4 flex flex-col gap-1 text-left hover:border-indigo-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-indigo-400 group"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <UserCircle2 className="w-5 h-5 text-indigo-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors" />
                </div>
                <p className="font-semibold text-gray-900">{member.name}</p>
                <p className="text-sm text-gray-500">{member.role}</p>
                <span
                  className={`inline-flex mt-1 self-start px-2 py-0.5 rounded-full text-xs font-medium ${
                    member.status === 'On Leave'
                      ? 'bg-yellow-50 text-yellow-700'
                      : 'bg-green-50 text-green-700'
                  }`}
                >
                  {member.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedMember && (
        <TeamMemberDetailModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </div>
  );
};

export default ManagerTeamMember;
